import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import _ from 'lodash';

import { SummaryContainer } from './css';

import FleetTracker from './fleetTracker';
import AssetTracker from './assetTracker';
import VideoTelematics from './videoTelematics';
import TattleSystem from './tattleSystem';
import AccessoriesTracker from './accessoriesTracker';
import IotPopupTracker from './iotPopupTracker';

const SummaryTracker = ({
  productSelected,
  rateplanList,
  hasAdditonalServices,
  ratePlans,
  additionalServices,
  addons,
  hardware,
  vehicleInfo,
  installServices = {},
  orderNow = {},
  geotabBundles = {},
  iotPopupProducts = []
}) => {
  const [hardwareModel, setHardwareModel] = useState();
  const [hardwareList, setHardwareList] = useState();
  const [rateplanTotalsList, setRateplanTotalsList] = useState([]);
  const [tracker, setTracker] = useState({
    fleetTrackerRateplanTotal: 0,
    assetTrackerRateplanTotal: 0,
    videoTelematicsTracker: 0,
    tattleSystemTracker: 0,
    ...(hasAdditonalServices && { additionalServicesTotal: 0 }),
    addonsTotal: 0,
    vehiclesTotal: 0
  });

  useEffect(() => {
    const hardwareTotalList = rateplanList.reduce((acc, current) => {
      if (!_.get(current, 'fields.isHeader', false)) {
        const rateplanType = _.get(current, 'fields.rateplanType', '');
        const devicesList = _.get(current, 'fields.deviceList', []).reduce(
          (deviceAcc, deviceCurrent) => {
            return {
              ...deviceAcc,
              [_.get(deviceCurrent, 'fields.deviceName', '')]: 0
            };
          },
          {}
        );
        return {
          ...acc,
          [rateplanType]: { ...acc[rateplanType], ...devicesList }
        };
      }
      return { ...acc };
    }, {});
    const rateplanTotals = rateplanList.reduce((acc, current) => {
      if (
        !_.get(current, 'fields.isHeader', false) &&
        _.get(current, 'fields.showRateplanInSummary', false)
      )
        acc.push({
          name: _.get(current, 'fields.rateplanName', ''),
          total: 0
        });
      return acc;
    }, []);
    setRateplanTotalsList(rateplanTotals);
    setHardwareModel(hardwareTotalList);
  }, []);

  useEffect(() => {
    if (hardwareModel) {
      let totalRateplanFleetTracker = 0;
      let totalRateplanAssetTracker = 0;
      let totalVideoTelematicsTracker = 0;
      let totalTattleSystemTracker = 0;
      let additionalServicesTotal = 0;
      let addonsTotal = 0;
      let vehiclesTotal = 0;
      const hardwareObj =
        hardwareModel &&
        Object.keys(hardwareModel).reduce((acc, currentRateplanType) => {
          const deviceList = Object.keys(
            hardwareModel[currentRateplanType]
          ).reduce(
            (deviceAcc, currentDevice) => ({
              ...deviceAcc,
              [currentDevice]: 0
            }),
            {}
          );
          return { ...acc, [currentRateplanType]: { ...deviceList } };
        }, {});
      /**
       * Calculating Rateplan total + included hardware count
       */
      Object.keys(ratePlans).forEach(plan => {
        Object.keys(ratePlans[plan]).forEach(device => {
          const ratePlanQuantity = ratePlans[plan][device].quantity || 0;
          const ratePlanType = Object.keys(hardwareObj).reduce(
            (acc, current) => {
              if (device in hardwareObj[current]) return current;
              return acc;
            },
            ''
          );

          if (ratePlanType && _.has(hardwareObj, `${ratePlanType}.${device}`))
            hardwareObj[ratePlanType][device] += ratePlanQuantity;
          totalRateplanFleetTracker +=
            ratePlanType === 'Fleet Tracker' ? ratePlanQuantity : 0;
          totalRateplanAssetTracker +=
            ratePlanType === 'Asset Tracker' ? ratePlanQuantity : 0;
          totalVideoTelematicsTracker +=
            ratePlanType === 'Video Telematics' ? ratePlanQuantity : 0;
          totalTattleSystemTracker +=
            ratePlanType === 'Tattle System' ? ratePlanQuantity : 0;
        });
      });

      /**
       * Calculating additional hardware count
       */
      Object.keys(hardware).forEach(device => {
        const ratePlanType = Object.keys(hardwareObj).reduce((acc, current) => {
          if (device in hardwareObj[current]) return current;
          return acc;
        }, '');

        if (hardwareObj[ratePlanType] && device in hardwareObj[ratePlanType]) {
          const hardwareQuantity = hardware[device].additionalQuantity || 0;
          // Only add additionalQuantity if this device has no rateplan quantities already counted
          if (hardwareObj[ratePlanType][device] === 0) {
            hardwareObj[ratePlanType][device] += hardwareQuantity;
          }
        }
      });
      /**
       * Calculating Additional Services count
       */
      if (hasAdditonalServices)
        Object.keys(additionalServices).forEach(additionalService => {
          const additionalServiceQuantity =
            additionalServices[additionalService].quantity || 0;
          additionalServicesTotal += additionalServiceQuantity;
        });
      /**
       * Calculating Addon's count
       */
      Object.keys(addons).forEach(addon => {
        const addonQuantity = addons[addon].quantity || 0;
        addonsTotal += addonQuantity;
      });
      /**
       * Calculating Vehicle Info count
       */
      vehicleInfo.forEach(info => {
        const vehicleQuantity = +info.quantity || 0;
        vehiclesTotal += vehicleQuantity;
      });

      setTracker({
        fleetTrackerRateplanTotal: totalRateplanFleetTracker,
        assetTrackerRateplanTotal: totalRateplanAssetTracker,
        videoTelematicsTracker: totalVideoTelematicsTracker,
        tattleSystemTracker: totalTattleSystemTracker,
        ...(hasAdditonalServices && { additionalServicesTotal }),
        addonsTotal,
        vehiclesTotal
      });

      setHardwareList(hardwareObj);
    }
    if (rateplanTotalsList.length && !_.isEmpty(ratePlans)) {
      const updatedTotalsList = rateplanTotalsList.map(item => {
        const { name } = item;
        const total =
          _.get(ratePlans, name, '') &&
          Object.keys(ratePlans[name]).reduce((acc, current) => {
            const { quantity } = ratePlans[name][current];
            return acc + quantity;
          }, 0);
        return { name, total: total || 0 };
      });
      setRateplanTotalsList(updatedTotalsList);
    }
  }, [hardwareModel, ratePlans, hardware, vehicleInfo, addons]);

  return (
    <>
      {(hardwareList ||
        (Array.isArray(iotPopupProducts) && iotPopupProducts.length > 0)) && (
        <SummaryContainer>
          {_.get(hardwareList, 'Fleet Tracker', '') && (
            <FleetTracker
              tracker={tracker}
              hardwareList={_.get(hardwareList, 'Fleet Tracker', {})}
            />
          )}
          {_.get(hardwareList, 'Asset Tracker', '') && (
            <AssetTracker
              tracker={tracker}
              hardwareList={_.get(hardwareList, 'Asset Tracker', {})}
            />
          )}
          {_.get(hardwareList, 'Video Telematics', '') && (
            <VideoTelematics
              tracker={tracker}
              hardwareList={_.get(hardwareList, 'Video Telematics', {})}
            />
          )}
          {_.get(hardwareList, 'Tattle System', '') && (
            <TattleSystem
              productSelected={productSelected}
              tracker={tracker}
              rateplanTotalsList={rateplanTotalsList}
              hardwareList={_.get(hardwareList, 'Tattle System', {})}
            />
          )}
          <AccessoriesTracker hardware={hardware} />
          <AccessoriesTracker
            hardware={installServices}
            sectionText="Installation Services"
            isInstallationService={true}
          />
          <AccessoriesTracker
            hardware={addons}
            sectionText="Add-ons"
            isInstallationService={true}
          />
          <AccessoriesTracker
            hardware={orderNow}
            sectionText="Order Now"
            isInstallationService={true}
          />
          <AccessoriesTracker
            hardware={geotabBundles}
            sectionText="Geotab Bundles"
            isInstallationService={true}
          />
          <IotPopupTracker products={iotPopupProducts} />
        </SummaryContainer>
      )}
    </>
  );
};

SummaryTracker.propTypes = {
  productSelected: PropTypes.string.isRequired,
  rateplanList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  ratePlans: PropTypes.shape({}).isRequired,
  hasAdditonalServices: PropTypes.shape({}).isRequired,
  additionalServices: PropTypes.shape({}).isRequired,
  addons: PropTypes.shape({}).isRequired,
  installServices: PropTypes.shape({}).isRequired,
  hardware: PropTypes.shape({}).isRequired,
  vehicleInfo: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  geotabBundles: PropTypes.shape({}),
  iotPopupProducts: PropTypes.arrayOf(PropTypes.shape({}))
};

export default SummaryTracker;
